/**
 * The stored-locale redirect decision.
 *
 * A NULL locale means "never chosen", so browser/URL detection keeps
 * winning. When the customer HAS chosen, we redirect rather than just
 * swapping the catalogue: switching the text alone would leave Spanish
 * content sitting at a /pt-br/ address, and that URL is what they copy,
 * bookmark and share.
 *
 * The stored choice beats the URL because it is the one the customer made
 * deliberately, and because the BFF composes push payloads from that same
 * column (ADR-0006) — letting them disagree would mean a notification in one
 * language deep-linking into a page in the other.
 *
 * Pure, so it is testable without a router or a mounted component.
 */
import { isSupportedLocale } from '../i18n/index.ts';
import { localeFromPathname, translatePath, type RouteId, type RouteParams } from '../i18n/routes.ts';

export interface LocaleRedirectInput {
  /** `portalUser?.locale` — unvalidated, hence the wide type. */
  storedLocale: string | null | undefined;
  pathname: string;
  routeId: RouteId | undefined;
  params: RouteParams;
}

/** The path to redirect to, or null if the URL already matches the stored choice. */
export function localeRedirectDecision(input: LocaleRedirectInput): string | null {
  if (!isSupportedLocale(input.storedLocale)) return null;
  if (localeFromPathname(input.pathname) === input.storedLocale) return null;

  return translatePath(input.routeId, input.storedLocale, input.params);
}
